/**
 * AI import progress on the order and designers screens, per order or sub-unit.
 * Reads GET /api/ai/commit/by-order/:orderId with the CRM login; renders nothing without an import.
 * The "not in SWI yet" counts come from GET /api/ai/review/:orderId, as the section below uses,
 * so the two agree. Reloads the drawings list, refreshes the Flashing PDF when done, and closes
 * on a timer.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Alert, LinearProgress } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, tokenManager } from '../config/api.config';
import { logger } from '../utils/logger';

// Poll every 2 s while the import is saving.
const POLL_MS = 2000;
// Show a finished import for 30 s when all went to SWI, 30 min when any failed or was
// saved without SWI (so it is not missed).
const SHOW_SAVED_FOR_MS = 30 * 1000;
const SHOW_FAILED_FOR_MS = 30 * 60 * 1000;
const needsAttention = (summary) => (summary?.failed || 0) > 0 || (summary?.savedWithoutSwi || 0) > 0;
const showFinishedForMs = (summary) => (needsAttention(summary) ? SHOW_FAILED_FOR_MS : SHOW_SAVED_FOR_MS);

// On first load, an import this recent still refreshes the PDF (page opened right after Submit).
const JUST_FINISHED_MS = 60 * 1000;

const dismissKey = (commitId) => `aiImportDismissed_${commitId}`;
const pdfNotifiedKey = (commitId, committed) => `aiImportPdfNotified_${commitId}_${committed}`;

// Same stale flag and event a hand-drawn save uses, so Documents regenerates. Once per import.
const notifyFlashingDrawingsChanged = (orderId, commitId, committed) => {
  try {
    if (localStorage.getItem(pdfNotifiedKey(commitId, committed)) === 'true') return;
    localStorage.setItem(pdfNotifiedKey(commitId, committed), 'true');
    localStorage.setItem(`flashingPdfStale:${orderId}`, '1');
  } catch (_) { /* the event below still reaches an open panel */ }
  window.dispatchEvent(new CustomEvent('flashing-drawings-changed', { detail: { orderId } }));
};

const readDismissed = (commitId) => {
  try {
    return localStorage.getItem(dismissKey(commitId)) === 'true';
  } catch (_) {
    return false;
  }
};

// orderNumber: the order or sub-unit being viewed (e.g. "IN101941.A1"); only its import is shown.
const AiImportStatusBanner = ({ orderId, orderNumber = null }) => {
  const [commit, setCommit] = useState(null);
  // What the "not in SWI yet" section below lists ({ toFix, toDraw }), from the same API,
  // so the two numbers always agree. null until loaded (then the import's own counts are used).
  const [waitingCounts, setWaitingCounts] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  // Bumped when a finished banner's time is up, so it re-renders and closes.
  const [, setExpired] = useState(0);
  const lastCommitted = useRef(null);
  const lastState = useRef(null);

  useEffect(() => {
    if (!orderId) return undefined;
    let stopped = false;
    let timer = null;
    lastCommitted.current = null;
    lastState.current = null;
    // A different order or sub-unit: drop the previous one's banner straight away.
    setCommit(null);
    setWaitingCounts(null);

    // Same request as AiImportReviewSection. On failure keep the import's own counts.
    const loadWaiting = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/ai/review/${orderId}`, {
          headers: { 'x-access-token': tokenManager.getToken(), Accept: 'application/json' },
          params: orderNumber ? { orderNumber } : undefined,
        });
        if (stopped) return;
        const data = res.data?.data;
        if (data) {
          setWaitingCounts({
            toFix: (data.savedWithoutSwi || []).length,
            toDraw: (data.needsDrawing || []).length,
          });
        }
      } catch (err) {
        logger.warn('[AiImportStatusBanner] could not load the drawings not in SWI yet', err);
      }
    };

    const load = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/ai/commit/by-order/${orderId}`, {
          headers: { 'x-access-token': tokenManager.getToken(), Accept: 'application/json' },
          params: orderNumber ? { orderNumber } : undefined,
        });
        if (stopped) return;
        const data = res.data?.data || null;

        if (data) {
          // New drawings saved: tell the drawings list to reload.
          const committed = data.summary?.committed || 0;
          if (lastCommitted.current !== null && committed > lastCommitted.current) {
            try {
              localStorage.setItem(`refreshDrawings_${orderId}`, 'true');
            } catch (_) { /* shown on next reload */ }
          }
          lastCommitted.current = committed;

          // Finished with drawings saved (seen running, or just finished): refresh the PDF.
          if (data.state === 'finished' && committed > 0) {
            const sawItRun = lastState.current !== null && lastState.current !== 'finished';
            const finishedAt = data.finishedAt ? new Date(data.finishedAt).getTime() : 0;
            const justFinished = finishedAt > 0 && Date.now() - finishedAt < JUST_FINISHED_MS;
            if (sawItRun || justFinished) {
              notifyFlashingDrawingsChanged(orderId, data.commitId, committed);
            }
          }
          lastState.current = data.state;

          setDismissed(readDismissed(data.commitId));
        }

        if (data) await loadWaiting();
        if (stopped) return;
        setCommit(data);
        if (data && data.state !== 'finished') timer = setTimeout(load, POLL_MS);
      } catch (err) {
        if (stopped) return;
        const status = err.response?.status;
        // 404 is normal: no import for this order.
        if (status === 404) {
          setCommit(null);
          return;
        }
        logger.error('[AiImportStatusBanner] could not load AI import progress', err);
        // Do not retry 4xx errors. Retry others slowly.
        if (status && status < 500) return;
        timer = setTimeout(load, POLL_MS * 5);
      }
    };

    // A drawing was deleted or changed on this page: reload so the counts match.
    const onDrawingsChanged = (e) => {
      const changedId = e?.detail?.orderId;
      if (changedId && String(changedId) !== String(orderId)) return;
      if (timer) clearTimeout(timer);
      timer = null;
      load();
    };
    window.addEventListener('flashing-drawings-changed', onDrawingsChanged);
    // A "draw by hand" drawing was dismissed (AiImportReviewSection). Not the event above,
    // which also regenerates the Flashing PDF.
    window.addEventListener('ai-import-review-changed', onDrawingsChanged);

    load();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('flashing-drawings-changed', onDrawingsChanged);
      window.removeEventListener('ai-import-review-changed', onDrawingsChanged);
    };
  }, [orderId, orderNumber]);

  // Close a finished banner when its time is up, even if the page stays open.
  useEffect(() => {
    if (!commit || commit.state !== 'finished' || !commit.finishedAt) return undefined;
    const closesAt = new Date(commit.finishedAt).getTime() + showFinishedForMs(commit.summary);
    const remaining = closesAt - Date.now();
    if (remaining <= 0) return undefined;
    const t = setTimeout(() => setExpired((n) => n + 1), remaining + 50);
    return () => clearTimeout(t);
  }, [commit]);

  if (!commit || !commit.summary) return null;

  const { total, committed, failed } = commit.summary;
  // As listed in the section below (every import on this order); else this import's own
  // counts (older servers do not send these).
  const savedWithoutSwi = waitingCounts ? waitingCounts.toFix : (commit.summary.savedWithoutSwi || 0);
  const needsDrawing = waitingCounts ? waitingCounts.toDraw : (commit.summary.needsDrawing || 0);
  const running = commit.state !== 'finished';
  // Saved drawings still on the order (older servers do not send it: assume all).
  const onOrder = typeof commit.summary.onOrder === 'number' ? commit.summary.onOrder : committed;
  const deletedSince = Math.max(0, committed - onOrder);

  if (!running) {
    const finishedAt = commit.finishedAt ? new Date(commit.finishedAt).getTime() : 0;
    const recent = finishedAt && Date.now() - finishedAt < showFinishedForMs(commit.summary);
    if (!recent || dismissed) return null;
    // Every imported drawing was deleted and none failed: nothing left to report.
    if (committed > 0 && onOrder === 0 && failed === 0) return null;
  }

  const onDismiss = () => {
    try {
      localStorage.setItem(dismissKey(commit.commitId), 'true');
    } catch (_) { /* dismiss for this view only */ }
    setDismissed(true);
  };

  // To draw by hand is listed in the "not in SWI yet" section, not here.
  const failedDrawings = (commit.drawings || []).filter((d) => d.state === 'failed' && d.errorCode !== 'NEEDS_DRAWING');
  const done = committed + failed;
  // Everything in the "not in SWI yet" section below: saved to fix, plus to draw by hand.
  // Counted the same way as that section's heading, so the two always agree.
  const waiting = savedWithoutSwi + needsDrawing;
  const waitingSplit = savedWithoutSwi > 0 && needsDrawing > 0
    ? ` (${savedWithoutSwi} to fix, ${needsDrawing} to draw by hand)`
    : (needsDrawing > 0 ? ' (to draw by hand)' : ' (to fix)');

  if (running) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <div>
          Importing drawings from AI: <strong>{committed} of {total}</strong> saved
          {failedDrawings.length > 0 ? `, ${failedDrawings.length} not saved` : ''}
          {waiting > 0 ? `, ${waiting} not in SWI yet${waitingSplit}` : ''}. You can keep working, the drawings appear here as they are saved.
        </div>
        <LinearProgress
          variant="determinate"
          value={total > 0 ? Math.round((done / total) * 100) : 0}
          sx={{ mt: 1 }}
        />
      </Alert>
    );
  }

  return (
    <Alert severity={needsAttention(commit.summary) ? 'warning' : 'success'} onClose={onDismiss} sx={{ mb: 2 }}>
      <div>
        AI import finished: <strong>{committed}</strong> drawing{committed === 1 ? '' : 's'} saved
        {deletedSince > 0 ? <>, <strong>{deletedSince}</strong> since deleted</> : ''}
        {failedDrawings.length > 0 ? <>, <strong>{failedDrawings.length}</strong> not saved</> : ''}.
        {waiting > 0 ? <> <strong>{waiting}</strong> not in SWI yet{waitingSplit}, check {waiting === 1 ? 'it' : 'them'} below.</> : ''}
      </div>
      {failedDrawings.length > 0 && (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {failedDrawings.map((d) => (
            <li key={d.drawingRef}>
              {/* "Drawing N (name), Tag X". Older imports show the tag only. */}
              {d.sheetNumber ? <strong>Drawing {d.sheetNumber}{d.name ? ` (${d.name})` : ''}</strong> : null}
              {d.sheetNumber && d.tags && d.tags.length > 0 ? ', ' : ''}
              {d.tags && d.tags.length > 0 ? `Tag ${d.tags.join(', ')}` : ''}
              {d.sheetNumber || (d.tags && d.tags.length > 0) ? ': ' : ''}
              {d.errorMessage || d.errorCode || 'not saved'}
            </li>
          ))}
        </ul>
      )}
    </Alert>
  );
};

export default AiImportStatusBanner;
